/**
 * Tiny i18n runtime. Deliberately not i18next: three locales and ~100 keys do
 * not justify 40 KB of library, and the whole point of the setup is that the
 * key set is a TypeScript type — an unknown key is a compile error.
 *
 * FR resolves to DE at runtime (the FR file holds sentinels only) and the shell
 * shows a banner saying so. Nothing silently renders `__FR_PENDING__`.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

import { de, type TranslationKey } from './de'
import { en } from './en'
import { fr } from './fr'

export type Locale = 'de' | 'en' | 'fr'
export const LOCALES: Locale[] = ['de', 'en', 'fr']
export const DEFAULT_LOCALE: Locale = 'de'

/** Locales that ship real strings. FR is scaffolding — see fr.ts. */
export const READY_LOCALES: Locale[] = ['de', 'en']

export const catalogues = { de, en, fr } as const

export type { TranslationKey }

export type Translate = (key: TranslationKey, vars?: Record<string, string | number>) => string

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  )
}

export function translate(
  locale: Locale,
  key: TranslationKey,
  vars?: Record<string, string | number>,
): string {
  const catalogue = READY_LOCALES.includes(locale) ? catalogues[locale] : catalogues[DEFAULT_LOCALE]
  return interpolate(catalogue[key] ?? de[key], vars)
}

interface I18nValue {
  locale: Locale
  /** The locale actually rendered — differs from `locale` while FR is pending. */
  effectiveLocale: Locale
  isPending: boolean
  setLocale: (locale: Locale) => void
  t: Translate
}

const I18nContext = createContext<I18nValue | null>(null)

export function I18nProvider({
  children,
  initialLocale = DEFAULT_LOCALE,
}: {
  children: ReactNode
  initialLocale?: Locale
}) {
  const [locale, setLocale] = useState<Locale>(initialLocale)

  const t = useCallback<Translate>((key, vars) => translate(locale, key, vars), [locale])

  const value = useMemo<I18nValue>(() => {
    const ready = READY_LOCALES.includes(locale)
    return {
      locale,
      effectiveLocale: ready ? locale : DEFAULT_LOCALE,
      isPending: !ready,
      setLocale,
      t,
    }
  }, [locale, t])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n muss innerhalb von <I18nProvider> verwendet werden.')
  return value
}
